import { cn } from '@quadralize/utils'
import { type HTMLMotionProps, motion } from 'framer-motion'
import * as React from 'react'

interface GlassCardProps extends HTMLMotionProps<'div'> {
  variant?: 'subtle' | 'default' | 'intense'
  blur?: 'sm' | 'md' | 'lg' | 'xl'
}

const FULL_ROTATION = 360

const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  ({ className, variant = 'default', blur = 'md', children, ...props }, ref) => {
    const blurClasses = {
      sm: 'backdrop-blur-sm',
      md: 'backdrop-blur-md',
      lg: 'backdrop-blur-lg',
      xl: 'backdrop-blur-xl',
    }

    const variantClasses = {
      subtle: 'bg-white/5 border-white/10 dark:bg-black/5 dark:border-white/5',
      default: 'bg-white/10 border-white/20 dark:bg-black/20 dark:border-white/10',
      intense: 'bg-white/20 border-white/30 dark:bg-black/40 dark:border-white/20',
    }

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className={cn(
          'relative overflow-hidden rounded-xl border p-6 shadow-xl',
          blurClasses[blur],
          variantClasses[variant],
          className,
        )}
        {...props}
      >
        {/* Subtle Gradient Overlay */}
        <div className="absolute inset-0 z-0 bg-linear-to-br from-white/5 to-transparent pointer-events-none" />

        {/* Animated Glow on Hover */}
        <div className="absolute -inset-full z-0 pointer-events-none overflow-hidden group-hover:block hidden">
          <motion.div
            animate={{
              rotate: [0, FULL_ROTATION],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: 'linear',
            }}
            className="absolute inset-0 bg-[conic-gradient(from_0deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)]"
          />
        </div>

        <div className="relative z-10">{children as React.ReactNode}</div>
      </motion.div>
    )
  },
)

GlassCard.displayName = 'GlassCard'

export { GlassCard, type GlassCardProps }
